import React, { useRef, useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { useForm, Controller } from "react-hook-form";
import { useToast } from "../hooks/use-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { Car,MapPin,BadgeCheck,CalendarClock,Headset,ShieldCheck,MousePointerClick,Truck,
  Trophy,Star,Award,ChevronRight,ChevronLeft,Clock, Loader2, Menu, X } from "lucide-react";
import { modelsData } from "../data/car360Data";
import SEO from "@/components/SEO";

/* ----------------------- VALIDATION ----------------------- */
const schema = yup.object().shape({
  serviceCenter: yup.string().required("Required"),
  date: yup.date().required("Required").typeError("Required"),
  time: yup.string().required("Required"),
  model: yup.string().required("Required"),
  year: yup.string().required("Required"),
  mileage: yup.string().required("Required"),
  licencePlate: yup.string().required("Required"),
  title: yup.string().required("Required"),
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  email: yup.string().email("Invalid email").required("Required"),
  phone: yup
    .string()
    .matches(/^[0-9]+$/, "Digits only")
    .min(8, "Min 8 digits")
    .required("Required"),
});

/* ----------------------- TRANSLATIONS ----------------------- */
const translations = {
  en: {
    dir: "ltr",
    nav: {
      models: "Models",
      about: "Who We Are",
      awards: "Awards & Recognition",
      faq: "FAQ's",
      book: "Book Now",
    },
    heroBtn: "Confirm Availability",
    bookTitle: "Book Appointment",
    sections: {
      service: "Service Center",
      dateTime: "Date & Time",
      vehicle: "Vehicle & Contact Details",
    },
    fields: {
      serviceCenter: "Select Service Center",
      time: "Time",
      model: "Model",
      year: "Year",
      mileage: "Mileage",
      plate: "License Plate",
      title: "Title",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      phone: "Phone",
    },
    titles: {
      chooseModel: "Choose Your Model",
      whyChoose: "Why Choose BAIC?",
      whoWeAre: "Who We Are",
      awards: "Awards & Recognition",
      faq: "Frequently Asked Questions",
      factory: "Beijing Factory",
    },
    why: [
      { title: "Wide Range of Vehicles", desc: "Economy, sedan & SUV options" },
      { title: "Multiple Locations", desc: "Across Oman & GCC" },
      { title: "Transparent Pricing", desc: "No hidden charges" },
      { title: "Flexible Plans", desc: "Daily, weekly & monthly" },
      { title: "24/7 Support", desc: "Always available" },
      { title: "Well Maintained Cars", desc: "Safe and reliable" },
      { title: "Easy Online Booking", desc: "Fast & simple process" },
      { title: "Doorstep Delivery", desc: "Convenient service" },
    ],
    who: [
      { label: "Founded In", value: "1958" },
      { label: "Top Automotive Groups", value: "Top 5" },
      { label: "Operating Revenue", value: "480 B" },
      { label: "Brand Value", value: "353.68 B" },
      { label: "Fortune Global 500 Rank", value: "192" },
      { label: "Daimler AG Shares", value: "9.98%" },
      { label: "Annual Yield", value: "34 M" },
    ],
    awardsList: [
      {
        title: "Top Gear",
        desc: "BAIC X55 won the 2023 Readers’ Choice Award.",
      },
      {
        title: "JD Power",
        desc: "Recognized for quality and customer satisfaction.",
      },
      {
        title: "IDA & BDC",
        desc: "Awarded for design and engineering excellence.",
      },
    ],
    faqs: [
      {
        q: "What documents are required to rent a BAIC vehicle?",
        a: "You need a valid driving license, residence ID or passport, and valid visa.",
      },
      {
        q: "Is insurance included in the rental price?",
        a: "Yes, all rentals include standard insurance coverage.",
      },
      {
        q: "Can I book online and get doorstep delivery?",
        a: "Yes, we provide online booking with doorstep delivery.",
      },
      {
        q: "Are there any hidden charges?",
        a: "No, we follow transparent pricing.",
      },
      {
        q: "Can I extend my rental?",
        a: "Yes, contact support before expiry.",
      },
      {
        q: "Do vehicles include roadside assistance?",
        a: "Yes, 24/7 roadside assistance is included.",
      },
    ],
    footerTitle: "BAIC Oman",
    footerDesc:
      "Over 60 years of automotive excellence delivering safety, innovation, and performance worldwide.",
    submit: "Book Appointment",
    processing: "Processing...",
  },

  ar: {
    dir: "rtl",
    nav: {
      models: "السيارات",
      about: "من نحن",
      awards: "الجوائز",
      faq: "الأسئلة الشائعة",
      book: "احجز الآن",
    },
    heroBtn: "تأكيد التوفر",
    bookTitle: "حجز موعد",
    sections: {
      service: "مركز الخدمة",
      dateTime: "التاريخ والوقت",
      vehicle: "بيانات السيارة والتواصل",
    },
    fields: {
      serviceCenter: "اختر مركز الخدمة",
      time: "الوقت",
      model: "الموديل",
      year: "السنة",
      mileage: "عدد الكيلومترات",
      plate: "رقم اللوحة",
      title: "اللقب",
      firstName: "الاسم الأول",
      lastName: "اسم العائلة",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف",
    },
    titles: {
      chooseModel: "اختر سيارتك",
      whyChoose: "لماذا تختار BAIC؟",
      whoWeAre: "من نحن",
      awards: "الجوائز والتكريمات",
      faq: "الأسئلة الشائعة",
      factory: "مصنع بكين",
    },
    why: [
      { title: "تشكيلة واسعة من السيارات", desc: "اقتصادية، سيدان وSUV" },
      { title: "فروع متعددة", desc: "في جميع أنحاء عمان والخليج" },
      { title: "أسعار شفافة", desc: "بدون رسوم مخفية" },
      { title: "خطط مرنة", desc: "يومي، أسبوعي وشهري" },
      { title: "دعم على مدار الساعة", desc: "نحن دائماً في خدمتك" },
      { title: "سيارات بحالة ممتازة", desc: "آمنة وموثوقة" },
      { title: "حجز إلكتروني سهل", desc: "خطوات سريعة وبسيطة" },
      { title: "توصيل حتى باب المنزل", desc: "خدمة مريحة" },
    ],
    who: [
      { label: "تأسست في", value: "1958" },
      { label: "من أكبر مجموعات السيارات", value: "Top 5" },
      { label: "الإيرادات التشغيلية", value: "480 مليار" },
      { label: "قيمة العلامة التجارية", value: "353.68 مليار" },
      { label: "ترتيب Fortune Global 500", value: "192" },
      { label: "حصة في Daimler AG", value: "9.98%" },
      { label: "الإنتاج السنوي", value: "34 مليون" },
    ],
    awardsList: [
      {
        title: "Top Gear",
        desc: "فازت BAIC X55 بجائزة اختيار القراء لعام 2023.",
      },
      {
        title: "JD Power",
        desc: "تكريم لجودة المنتج ورضا العملاء.",
      },
      {
        title: "IDA & BDC",
        desc: "جوائز للابتكار في التصميم والهندسة.",
      },
    ],
    faqs: [
      {
        q: "ما هي المستندات المطلوبة لاستئجار سيارة BAIC؟",
        a: "رخصة قيادة سارية، بطاقة إقامة أو جواز سفر وتأشيرة صالحة.",
      },
      {
        q: "هل التأمين مشمول في السعر؟",
        a: "نعم، جميع الإيجارات تشمل تأميناً أساسياً.",
      },
      {
        q: "هل يمكنني الحجز عبر الإنترنت مع التوصيل للمنزل؟",
        a: "نعم، نوفر حجزاً إلكترونياً مع خدمة التوصيل.",
      },
      {
        q: "هل توجد رسوم مخفية؟",
        a: "لا، نتبع سياسة تسعير شفافة.",
      },
      {
        q: "هل يمكن تمديد فترة الإيجار؟",
        a: "نعم، يرجى التواصل مع الدعم قبل انتهاء الحجز.",
      },
      {
        q: "هل تتوفر خدمة إزالة الأعطال على الطريق؟",
        a: "نعم، تتوفر خدمة المساعدة على الطريق 24/7.",
      },
    ],
    footerTitle: "BAIC عمان",
    footerDesc:
      "أكثر من 60 عاماً من التميز في صناعة السيارات، نقدم الجودة والأمان والابتكار حول العالم.",
    submit: "احجز موعداً",
    processing: "جارٍ المعالجة...",
  },
};

/* ----------------------- COMPONENT ----------------------- */
const LandingPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const banner = location.state;
  const modelParam = banner?.title || decodeURIComponent(id || "");
  const image = banner?.image || "/assets/images/homepage/banners/BAIC-Range.jpg";

  const [lang, setLang] = useState("en");
  const t = translations[lang];
  const isRTL = t.dir === "rtl";

  const [openIndex, setOpenIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const formRef = useRef(null);

  /* ======= Section Refs ======= */
  const modelsRef = useRef(null);
  const whoWeAreRef = useRef(null);
  const awardsRef = useRef(null);
  const faqRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false); // close mobile menu
  };

  useEffect(() => {
    setShowModal(true);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      serviceCenter: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      model: modelParam,
      year: "",
      mileage: "",
      licencePlate: "",
      date: "",
      time: "",
      title: "Mr",
    },
  });

  const selectedDate = watch("date");

  const serviceCenters = [
    "Azaiba Service Center",
    "Wadi Kabir Service Center",
    "Al Amerat Service Center",
    "Mabella Service Center",
    "Rusayl – Bodyshop",
    "Barka Service Center",
    "Suhar Service Center",
    "Nizwa Service Center",
    "Ibri Service Center",
    "Sur Service Center",
    "Duqm Service Center",
    "Maweleh Service Center",
    "Salalah Service Center",
  ];

  const models = ["BJ30 Hybrid", "BJ40 Honor", "BJ40 PRO", "U5 PLUS"];
  const years = Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - i);

  const handleEnquire = (modelName) => {
    setValue("model", modelName, { shouldValidate: true });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const nextMonth = () => {
    const current = new Date(selectedDate || new Date());
    const next = new Date(current);
    next.setMonth(current.getMonth() + 1);
    setValue("date", next);
  };

  const prevMonth = () => {
    const current = new Date(selectedDate || new Date());
    const prev = new Date(current);
    prev.setMonth(current.getMonth() - 1);
    setValue("date", prev);
  };

  const onSubmit = async (data) => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (recaptchaSiteKey && !captchaToken) {
      toast({
        title: "Validation Error",
        description: "Please complete the ReCAPTCHA verification.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("serviceCenter", data.serviceCenter);
      const d = new Date(data.date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      formData.append("date", `${day}-${month}-${year}`);
      formData.append("time", data.time);
      formData.append("model_id", data.model);
      formData.append("year", data.year);
      formData.append("mileage", data.mileage);
      formData.append("licencePlate", data.licencePlate);
      formData.append("title", data.title);
      formData.append("firstName", data.firstName);
      formData.append("lastName", data.lastName);
      formData.append("email", data.email);
      formData.append("phone", data.phone);
      formData.append("recaptcha_token", captchaToken);

      const response = await axios.post(
        "https://admin.baicoman.com/api/service-leads",
        formData
      );

      if (response.status === 200 || response.status === 201) {
        toast({
          title: "Success",
          description: "Your service appointment has been booked successfully.",
          className: "bg-green-600 text-white border-none",
        });
        reset({ model: data.model });
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, msg]) => {
          setError(field, { type: "server", message: msg[0] });
        });
      }
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModelName = (id) => modelsData[id]?.name || id;

  return (
    <main className={`min-h-screen bg-white ${isRTL ? "rtl" : "ltr"}`} dir={t.dir}>
       <SEO
          title="BAIC OMAN – Premium Solutions & Services Landing Page"
          description="Explore BAICOMAN’s landing page for premium solutions, key offerings, and value-driven insights to connect with our brand and services online."
          canonicalUrl="https://baicoman.com/landing-page"
        />
      {/* ================= NAVBAR ================= */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <img
              src="/assets/baic-logo.webp"
              alt="BAIC"
              className="h-10 w-auto object-contain"
            />
          </div>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center gap-10 text-base font-semibold text-black">
            <button
              onClick={() => scrollToSection(modelsRef)}
              className="hover:text-red-600 transition-colors"
            >
              {t.nav.models}
            </button>
            <button
              onClick={() => scrollToSection(whoWeAreRef)}
              className="hover:text-red-600 transition-colors"
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection(awardsRef)}
              className="hover:text-red-600 transition-colors"
            >
              {t.nav.awards}
            </button>
            <button
              onClick={() => scrollToSection(faqRef)}
              className="hover:text-red-600 transition-colors"
            >
              {t.nav.faq}
            </button>
          </nav>

          {/* Right Controls */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <Button variant="outline"  onClick={() => setLang(lang === "en" ? "ar" : "en")}
            className="border-2 rounded-full px-6 uppercase tracking-wider font-bold transition-all duration-300 border-red-600 text-red-600 hover:bg-red-600 hover:text-white" >
                          {lang === 'en' ? 'AR' : 'EN'}
                        </Button>
         

            {/* Mobile Toggle */}
            <button
              className="md:hidden text-gray-700"
              onClick={() => setOpen(!open)}
            >
              {open ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {open && (
          <div className="md:hidden bg-white border-t shadow-md">
            <nav className="flex flex-col py-4 px-6 space-y-4 font-semibold text-gray-700">
              <button onClick={() => scrollToSection(modelsRef)}>
                {t.nav.models}
              </button>
              <button onClick={() => scrollToSection(whoWeAreRef)}>
                {t.nav.about}
              </button>
              <button onClick={() => scrollToSection(awardsRef)}>
                {t.nav.awards}
              </button>
              <button onClick={() => scrollToSection(faqRef)}>
                {t.nav.faq}
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Spacer */}
      <div className="h-20" />

      {/* ================= AUTO MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-[90%] max-w-md p-8 relative shadow-2xl animate-in fade-in zoom-in">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-black text-xl"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold text-center mb-6">
              {t.bookTitle}
            </h2>

            <form className="space-y-4">
              <Input placeholder={t.fields.firstName} />
              <Input type="email" placeholder={t.fields.email} />
              <Input type="tel" placeholder={t.fields.phone} />
              <Input placeholder="City" />
              <Button
                size="lg"
                className="rounded-full w-full text-white bg-black hover:bg-red-600 hover:text-white transition-all font-bold shadow-xl hover:-translate-y-1"
              >
                {t.submit}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* ================= HERO ================= */}
      <div className="relative h-full w-full">
        <img
          src={image}
          alt={modelParam}
          className="w-full h-full object-cover"
        />
        <div className="absolute z-20 inset-0 flex items-end">
          <div
            className={`max-w-6xl absolute bottom-6 ${
              isRTL ? "right-6" : "left-6"
            } mx-auto px-6 pb-8`}
          >
            <Button
              onClick={() =>
                formRef.current?.scrollIntoView({ behavior: "smooth" })
              }
              className="bg-white text-black hover:bg-gray-100 rounded-full px-6 py-3 text-sm font-semibold shadow-lg"
            >
              {t.heroBtn}
            </Button>
          </div>
        </div>
      </div>

      {/* ================= BOOK FORM ================= */}
      <section ref={formRef} className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="text-2xl font-bold text-center mb-12">
          {t.bookTitle}
        </h2>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="max-w-4xl mx-auto space-y-16"
        >
          {/* ---------- SERVICE CENTER ---------- */}
          <div className="space-y-8 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold">
                1
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t.sections.service}
              </h2>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 ml-1">
                {t.fields.serviceCenter}
              </label>
              <div className="relative">
                <select
                  {...register("serviceCenter")}
                  className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 cursor-pointer appearance-none"
                >
                  <option value="">{t.fields.serviceCenter}</option>
                  {serviceCenters.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div
                  className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${
                    isRTL ? "left-4" : "right-4"
                  }`}
                >
                  <ChevronRight className="w-5 h-5 text-gray-400 rotate-90" />
                </div>
              </div>
              {errors.serviceCenter && (
                <p className="text-red-500 text-xs ml-1">
                  {errors.serviceCenter.message}
                </p>
              )}
            </div>
          </div>

          {/* ---------- DATE & TIME ---------- */}
          <div className="space-y-8 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold">
                2
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t.sections.dateTime}
              </h2>
            </div>

            <div className="flex flex-col lg:flex-row gap-12">
              <div className="flex-1">
                <div className="bg-[#1a1a1a] text-white p-8 rounded-3xl shadow-xl">
                  <div className="flex justify-between items-center mb-6">
                    <button
                      type="button"
                      onClick={prevMonth}
                      className="hover:text-red-500 transition-colors p-2 hover:bg-white/10 rounded-full"
                    >
                      {isRTL ? (
                        <ChevronRight className="h-5 w-5" />
                      ) : (
                        <ChevronLeft className="h-5 w-5" />
                      )}
                    </button>
                    <span className="font-bold text-lg tracking-wide">
                      {selectedDate instanceof Date && !isNaN(selectedDate)
                        ? selectedDate.toLocaleString(lang === "ar" ? "ar" : "en", {
                            month: "long",
                            year: "numeric",
                          })
                        : new Date().toLocaleString(lang === "ar" ? "ar" : "en", {
                            month: "long",
                            year: "numeric",
                          })}
                    </span>
                    <button
                      type="button"
                      onClick={nextMonth}
                      className="hover:text-red-500 transition-colors p-2 hover:bg-white/10 rounded-full"
                    >
                      {isRTL ? (
                        <ChevronLeft className="h-5 w-5" />
                      ) : (
                        <ChevronRight className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  <Controller
                    control={control}
                    name="date"
                    render={({ field }) => (
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        className="rounded-md border-none w-full text-white"
                        classNames={{
                          day_selected:
                            "bg-red-600 text-white hover:bg-red-600 focus:bg-red-600 shadow-lg shadow-red-600/30",
                          day_today:
                            "bg-white/10 text-white font-bold",
                          day:
                            "text-white/80 hover:bg-white/20 hover:text-white rounded-full w-10 h-10 transition-all duration-200",
                          head_cell:
                            "text-gray-500 w-10 font-medium text-xs uppercase tracking-wider mb-2",
                          caption: "hidden",
                          table: "w-full border-collapse space-y-1",
                          nav_button: "hidden",
                        }}
                      />
                    )}
                  />
                  {errors.date && (
                    <p className="text-red-500 text-xs mt-2">
                      {errors.date.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="lg:w-1/3 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> {t.fields.time}
                  </label>
                  <input
                    type="time"
                    {...register("time")}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100"
                  />
                  {errors.time && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.time.message}
                    </p>
                  )}
                </div>
                <div className="bg-blue-50 p-6 rounded-2xl text-blue-900 text-sm leading-relaxed">
                  <p>
                    <strong>Note:</strong> Appointment slots are subject to
                    availability.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ---------- VEHICLE & CONTACT ---------- */}
          <div className="space-y-8 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold">
                3
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t.sections.vehicle}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Vehicle Info */}
              <div className="space-y-6">
                <h3 className="font-bold text-gray-400 uppercase tracking-widest text-xs mb-4 border-b pb-2">
                  {lang === "ar" ? "بيانات السيارة" : "Vehicle Info"}
                </h3>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    {t.fields.model}
                  </label>
                  <select
                    {...register("model")}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 appearance-none"
                  >
                    <option value="">{t.fields.model}</option>
                    {models.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  {errors.model && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.model.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    {t.fields.year}
                  </label>
                  <select
                    {...register("year")}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 appearance-none"
                  >
                    <option value="">{t.fields.year}</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  {errors.year && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.year.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    {t.fields.mileage}
                  </label>
                  <input
                    {...register("mileage")}
                    type="text"
                    placeholder={t.fields.mileage}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400"
                  />
                  {errors.mileage && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.mileage.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    {t.fields.plate}
                  </label>
                  <input
                    {...register("licencePlate")}
                    type="text"
                    placeholder={t.fields.plate}
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400"
                  />
                  {errors.licencePlate && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.licencePlate.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-6">
                <h3 className="font-bold text-gray-400 uppercase tracking-widest text-xs mb-4 border-b pb-2">
                  {lang === "ar" ? "بيانات التواصل" : "Contact Info"}
                </h3>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-1 space-y-2">
                    <label className="text-sm font-semibold text-gray-700 ml-1">
                      {t.fields.title}
                    </label>
                    <select
                      {...register("title")}
                      className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 appearance-none"
                    >
                      <option value="Mr">
                        {lang === "ar" ? "السيد" : "Mr."}
                      </option>
                      <option value="Ms">
                        {lang === "ar" ? "الآنسة" : "Ms."}
                      </option>
                      <option value="Mrs">
                        {lang === "ar" ? "السيدة" : "Mrs."}
                      </option>
                    </select>
                    {errors.title && (
                      <p className="text-red-500 text-xs ml-1">
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  <div className="col-span-2 space-y-2">
                    <label className="text-sm font-semibold text-gray-700 ml-1">
                      {t.fields.firstName}
                    </label>
                    <input
                      {...register("firstName")}
                      type="text"
                      className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs ml-1">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    {t.fields.lastName}
                  </label>
                  <input
                    {...register("lastName")}
                    type="text"
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    {t.fields.email}
                  </label>
                  <input
                    {...register("email")}
                    type="email"
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    {t.fields.phone}
                  </label>
                  <input
                    {...register("phone")}
                    type="tel"
                    className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ---------- SUBMIT ---------- */}
          <div className="pt-8 flex flex-col items-center space-y-8">
            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
              <div className="w-full flex flex-col items-center gap-2">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                />
              </div>
            )}

            <Button
              disabled={isSubmitting}
              type="submit"
              size="lg"
              className="w-full md:w-auto min-w-[300px] rounded-full px-12 py-8 bg-black text-white hover:bg-red-600 border-none transition-all duration-300 text-lg font-bold shadow-xl hover:shadow-red-600/30 hover:-translate-y-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t.processing}
                </>
              ) : (
                t.submit
              )}
            </Button>
          </div>
        </form>
      </section>

      {/* ================= MODELS ================= */}
      <section ref={modelsRef} className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
            {t.titles.chooseModel}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(modelsData).map(([modelId, model]) => {
              const modelName = getModelName(modelId);
              return (
                <div
                  key={modelId}
                  className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img
                      src={model.image}
                      alt={modelName}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  <div className="p-6 text-center">
                    <h3 className="text-lg font-bold mb-4">{modelName}</h3>
                    <button
                      onClick={() => handleEnquire(modelName)}
                      className="inline-flex items-center justify-center px-6 py-2 rounded-full bg-black text-white text-sm font-semibold hover:bg-red-600 transition-all duration-300"
                    >
                      {t.nav.book}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE ================= */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
            {t.titles.whyChoose}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.why.map((item, i) => {
              const icons = [
                Car,
                MapPin,
                BadgeCheck,
                CalendarClock,
                Headset,
                ShieldCheck,
                MousePointerClick,
                Truck,
              ];
              const Icon = icons[i];
              return (
                <div
                  key={i}
                  className="bg-gray-50 rounded-3xl p-8 text-center shadow-sm hover:shadow-xl transition-all"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= WHO WE ARE ================= */}
      <section ref={whoWeAreRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
            {t.titles.whoWeAre}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
            {t.who.map((stat, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-all"
              >
                <p className="text-3xl font-bold text-red-600 mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-600 text-sm font-semibold">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

       {/* ================= IMAGE-STYLE FOOTER ================= */}
      <section className="bg-[#f4f7fb] py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-[2rem] shadow-xl p-10 md:p-14 text-center space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              BAIC Oman
            </h2>

            <p className="text-gray-700 leading-relaxed text-base md:text-lg max-w-4xl mx-auto">
              Our commitment to safety, quality, and performance continues to earn
              global recognition across powertrain technology, chassis development,
              intelligent driving, cabin control, and engineering materials.
            </p>

            <Button
              disabled={isSubmitting}
              type="submit"
              size="lg"
              className="w-full md:w-auto min-w-[300px] rounded-full px-12 py-8 bg-black text-white hover:bg-red-600 border-none transition-all duration-300 text-lg font-bold shadow-xl hover:shadow-red-600/30 hover:-translate-y-1"
              onClick={() =>
                document
                  .querySelector("form")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Book now with BAIC Oman
            </Button>
          </div>
        </div>
      </section>

      {/* ================= AWARDS ================= */}
      <section ref={awardsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
            {t.titles.awards}
          </h2>

          <p className="max-w-4xl mx-auto text-center text-gray-600 mb-14">
            {lang === "ar"
              ? "على مدار أكثر من 60 عاماً، حصلت BAIC على العديد من الجوائز في مجالات السلامة والتكنولوجيا والهندسة."
              : "Over 60 years of innovation, BAIC has received multiple global awards in safety, powertrain, and engineering excellence."}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {t.awardsList.map((award, i) => {
              const icons = [Trophy, Star, Award];
              const Icon = icons[i];
              return (
                <div
                  key={i}
                  className="bg-gray-50 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h4 className="font-bold text-lg mb-3">{award.title}</h4>
                  <p className="text-gray-600 text-sm">{award.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= FAQs ================= */}
      <section ref={faqRef} className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
            {t.titles.faq}
          </h2>

          <div className="space-y-5">
            {t.faqs.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-900 text-base md:text-lg">
                      {item.q}
                    </h4>
                    <span
                      className={`text-red-600 text-xl transition-transform ${
                        isOpen ? "rotate-45" : ""
                      }`}
                    >
                      +
                    </span>
                  </div>
                  {isOpen && (
                    <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">
                      {item.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= BEIJING FACTORY ================= */}
      <section className="bg-gray-50 mb-20">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
            {t.titles.factory}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                {lang === "ar"
                  ? "يمثل مصنع BAIC في بكين أحدث تقنيات التصنيع الذكي مع التركيز على الجودة والاستدامة."
                  : "BAIC’s Beijing manufacturing facility represents cutting-edge automotive production focused on intelligent manufacturing, quality, and sustainability."}
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  {
                    label:
                      lang === "ar"
                        ? "بداية الإنتاج"
                        : "Production Start Time",
                    value: "2015",
                  },
                  {
                    label: lang === "ar" ? "مساحة الأرض" : "Land Area",
                    value: "540,000 sqm",
                  },
                  {
                    label:
                      lang === "ar"
                        ? "الطاقة الإنتاجية"
                        : "Planned Capacity",
                    value: "100,000 units / year",
                  },
                  {
                    label:
                      lang === "ar"
                        ? "الطرازات المنتجة"
                        : "Models In Production",
                    value:
                      "BJ30 Hybrid / BJ40 Honor / BJ40 Pro / U5 Plus",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all"
                  >
                    <p className="text-xl font-bold text-red-600 mb-1">
                      {item.value}
                    </p>
                    <p className="text-gray-600 text-sm font-semibold">
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="/assets/images/about/overview/new/BEIJING.webp"
                alt="Beijing Factory"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="relative overflow-hidden bg-[#0b0f19] mt-10 py-20 sm:py-24 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 via-transparent to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center bg-white/5 backdrop-blur-xl rounded-[2.5rem] p-8 sm:p-12 lg:p-16 shadow-2xl border border-white/10">
            <div className="space-y-6 text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {t.footerTitle}
              </h2>

              <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t.footerDesc}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2">
                <Button
                  size="lg"
                  className="rounded-full px-10 py-7 text-white bg-black hover:bg-red-600 hover:text-white transition-all font-bold shadow-xl hover:-translate-y-1"
                  onClick={() =>
                    document
                      .querySelector("form")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  {t.submit}
                </Button>
              </div>
            </div>

            <div className="relative h-[260px] sm:h-[320px] lg:h-[380px] rounded-[2rem] overflow-hidden shadow-2xl">
              <img
                src="/assets/images/about/overview/new/BEIJING.webp"
                alt="BAIC Manufacturing"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md rounded-2xl p-5 sm:p-6 shadow-lg">
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  {lang === "ar" ? (
                    <>
                      أكثر من{" "}
                      <span className="text-red-600 font-bold">
                        60 عاماً
                      </span>{" "}
                      من التميز
                    </>
                  ) : (
                    <>
                      Over{" "}
                      <span className="text-red-600 font-bold">
                        60 years
                      </span>{" "}
                      of excellence
                    </>
                  )}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {lang === "ar"
                    ? "ثقة، أداء وابتكار عالمي"
                    : "Engineering trust, performance, and innovation worldwide"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
